'use client';

import { useEffect, useState } from 'react';
import { Crown, X } from 'lucide-react';
import { api, type Usage } from '@/lib/api';
import { SupportButton } from './SupportLink';

// 個人設置的會員區塊:當前等級 + 到期日 + 自訂權限摘要,「詳情」彈窗解說各
// 等級權限並引導聯繫客服開通(人工收費模式,無自助金流)。
const PLAN_NAME: Record<string, string> = { free: '免費會員', pro_max: 'Pro Max' };

const CAT_LABEL: Record<string, string> = {
  METAL: '黃金',
  CRYPTO: '虛擬貨幣',
  TW_STOCK: '台股',
  US_STOCK: '美股',
};

const TIERS = [
  { name: '免費會員', price: 'NT$0', points: ['AI 助理 3 則/日', '專欄試看 1 篇/月'] },
  {
    name: 'Pro Max',
    price: 'NT$499/月 · 年繳 NT$4,500',
    points: ['AI 助理 100 則/日', '專家專欄即時分享', '分享買賣與目標價建議'],
  },
];

function fmtDate(iso: string | null | undefined) {
  if (!iso) return null;
  const d = new Date(iso);
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

export default function MembershipSummary() {
  const [u, setU] = useState<Usage | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.usage().then(setU).catch(() => {});
  }, []);

  const planEnd = fmtDate(u?.plan_end);
  const entEnd = fmtDate(u?.ent_expires);
  const cats = u?.column_categories;
  const hasCustom = u ? cats !== null && cats !== undefined : false;

  return (
    <section className="mb-4 rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-sm font-medium text-zinc-500">
          <Crown size={14} className="text-amber-500" /> 會員等級
        </h2>
        <button onClick={() => setOpen(true)} className="text-xs font-medium text-brand">
          詳情 →
        </button>
      </div>

      {u ? (
        <div className="mt-2">
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold">{PLAN_NAME[u.plan] ?? u.plan}</span>
            {planEnd && <span className="text-xs text-zinc-400">有效至 {planEnd}</span>}
          </div>
          {hasCustom && (
            <p className="mt-1.5 text-xs text-zinc-500">
              已開通:AI {u.daily_questions} 則/日
              {cats && cats.length > 0
                ? ` · 專欄「${cats.map((k) => CAT_LABEL[k] ?? k).join('、')}」無限閱讀`
                : ''}
              {entEnd ? `(至 ${entEnd})` : ''}
            </p>
          )}
        </div>
      ) : (
        <div className="mt-2 h-7 w-28 animate-pulse rounded bg-zinc-100 dark:bg-zinc-800" />
      )}

      {/* 詳情彈窗 */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
          <div className="absolute inset-0 bg-black/45" onClick={() => setOpen(false)} />
          <div className="relative z-10 max-h-[85dvh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white p-5 pb-8 shadow-2xl dark:bg-zinc-900 sm:rounded-3xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">會員方案說明</h3>
              <button
                onClick={() => setOpen(false)}
                aria-label="關閉"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-100 text-zinc-500 dark:bg-zinc-800"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-3">
              {TIERS.map((tier) => {
                const current = (PLAN_NAME[u?.plan ?? ''] ?? '') === tier.name;
                return (
                  <div
                    key={tier.name}
                    className={`rounded-2xl border p-4 ${
                      current
                        ? 'border-brand/60 bg-brand/5'
                        : 'border-zinc-200 dark:border-zinc-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold">{tier.name}</span>
                      {current && (
                        <span className="rounded-full bg-brand px-2 py-0.5 text-[10px] font-semibold text-white">
                          目前方案
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-zinc-400">{tier.price}</p>
                    <ul className="mt-2 space-y-1 text-sm text-zinc-600 dark:text-zinc-300">
                      {tier.points.map((p) => (
                        <li key={p} className="flex items-center gap-1.5">
                          <span className="h-1 w-1 rounded-full bg-emerald-500" /> {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            <p className="mt-4 text-xs leading-relaxed text-zinc-500">
              另有加值方案:加開特定專欄老師的即時分享與加購 AI
              分析則數。開通與續費請聯繫客服,由專人為你設定。
            </p>
            <div className="mt-3">
              <SupportButton />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
